import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { CommunityAdmin } from "@/components/CommunityAdmin";
import { RoleManager } from "@/components/RoleManager";
import {
  ShieldCheck,
  Users,
  UserPlus,
  FileText,
  MousePointerClick,
  Unlock,
  TrendingUp,
  Repeat,
  Loader2,
  LogOut,
  Eye,
  ShieldAlert,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANGES = [
  { id: "day", label: "Hari ini" },
  { id: "week", label: "7 Hari" },
  { id: "month", label: "30 Hari" },
];

function Metric({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5" data-testid={`metric-${label}`}>
      <div className="flex items-center gap-2 text-zinc-400">
        <Icon className="h-4 w-4" />
        <span className="text-[11px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className={`mt-3 font-mono text-3xl font-black ${accent || "text-white"}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading, isAdmin, logout } = useAuth();
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (r) => {
    setFetching(true);
    setError(null);
    try {
      const d = await api.adminMetrics(r);
      setData(d);
    } catch (e) {
      setError(e.message || "Gagal memuat data");
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load(range);
  }, [isAdmin, range, load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!isAdmin) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-md lg:max-w-6xl flex-col items-center justify-center bg-zinc-950 px-8 text-center" data-testid="admin-denied">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-500/15">
          <ShieldAlert className="h-8 w-8 text-rose-400" />
        </div>
        <h1 className="mt-6 font-heading text-xl font-extrabold text-white">Anda bukan admin</h1>
        <p className="mt-2 text-sm leading-relaxed text-zinc-400">
          Halaman ini khusus admin CeKKeU. Akun Anda tidak memiliki akses ke dashboard admin.
        </p>
        <button
          data-testid="admin-denied-home"
          onClick={() => navigate("/")}
          className="mt-8 h-12 w-full rounded-full bg-white text-sm font-semibold text-zinc-900 hover:bg-zinc-200"
        >
          Kembali ke Beranda
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-6xl bg-zinc-950 px-6 pb-16 pt-8" data-testid="admin-dashboard">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-zinc-900">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="font-heading text-sm font-bold text-white">Admin</p>
            <p className="text-[11px] text-zinc-500">{user.email}</p>
          </div>
        </div>
        <button
          data-testid="admin-logout"
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          className="flex items-center gap-1.5 rounded-full border border-zinc-800 px-3 py-1.5 text-xs font-semibold text-zinc-300"
        >
          <LogOut className="h-3.5 w-3.5" /> Keluar
        </button>
      </header>

      <h1 className="mt-6 font-heading text-2xl font-extrabold text-white">Metrik Produk</h1>
      <p className="mt-1 text-sm text-zinc-500">Ringkasan tanpa data keuangan pribadi apa pun.</p>

      <div className="mt-5 flex gap-2 rounded-full bg-zinc-900 p-1">
        {RANGES.map((r) => (
          <button
            key={r.id}
            data-testid={`admin-range-${r.id}`}
            onClick={() => setRange(r.id)}
            className={`flex-1 rounded-full py-2 text-xs font-semibold transition-colors ${
              range === r.id ? "bg-emerald-500 text-zinc-900" : "text-zinc-400"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {fetching ? (
        <div className="mt-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
        </div>
      ) : error ? (
        <div className="mt-6 rounded-2xl border border-rose-900 bg-rose-950/50 p-4 text-sm text-rose-300" data-testid="admin-error">
          {error}
        </div>
      ) : data ? (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Metric icon={Eye} label="Landing Views" value={data.landingViews} />
          <Metric icon={Users} label="Pengunjung Unik" value={data.uniqueVisitors} />
          <Metric icon={UserPlus} label="Daftar Baru" value={data.registeredUsers} sub={`Total: ${data.totalUsers}`} />
          <Metric icon={Repeat} label="Pengunjung Balik" value={data.returningUsers} />
          <Metric icon={FileText} label="Tes Dimulai" value={data.assessmentsStarted} />
          <Metric
            icon={TrendingUp}
            label="Tes Selesai"
            value={data.assessmentsCompleted}
            sub={`Konversi ${data.completionRate}%`}
            accent="text-emerald-400"
          />
          <Metric icon={Eye} label="Laporan Dilihat" value={data.reportsViewed} />
          <Metric icon={MousePointerClick} label="Klik Premium" value={data.premiumClicks} />
          <Metric icon={Unlock} label="Premium Unlock" value={data.premiumUnlocks} accent="text-emerald-400" />
          <Metric
            icon={TrendingUp}
            label="Visitor → Daftar"
            value={`${data.visitorToRegistration}%`}
            accent="text-emerald-400"
          />
        </div>
      ) : null}

      <CommunityAdmin />
      {user?.role === "super_admin" && <RoleManager />}
    </div>
  );
}
